import api from './axios';

/**
 * Members (spec §8.5). `transferUpline` is Super-Admin-only and the server
 * refuses it once the member has activated — surface the 422 message verbatim,
 * it is the wording the spec mandates.
 */
export const listMembers    = (params = {})        => api.get('/v1/members', { params });

/**
 * Admin-desk registration — the walk-in counterpart to the public referral form.
 * The new member lands Inactive with an unpaid Membership Fee at whatever the
 * live fee schedule says (Rule 74); nothing
 * here can activate them. A Branch Admin's branch is taken from their principal
 * server-side, so `branch_id` in the body is advisory for that caller.
 */
export const createMember = (payload) => api.post('/v1/members', payload, {
  // A retried POST must not produce a second member and a second bill (Rule 29).
  headers: { 'Idempotency-Key': (crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`) },
});
export const getMyMember    = ()                   => api.get('/v1/members/me');
export const getMember      = (memberId)           => api.get(`/v1/members/${memberId}`);

/**
 * The member's OWN beneficiary list. No id in the path — the server resolves
 * the row from the token, so there is nothing here to point at anyone else.
 * Sending the array replaces the whole list.
 */
export const updateMyBeneficiaries = (beneficiaries) =>
  api.put('/v1/members/me/beneficiaries', { beneficiaries });

/**
 * Partial profile edit — send only what changed. The lifecycle fields and the
 * upline are not accepted here; the server ignores them if sent.
 */
export const updateMember   = (memberId, payload)  => api.put(`/v1/members/${memberId}`, payload);
export const transferUpline = (memberId, payload)  => api.post(`/v1/members/${memberId}/transfer-upline`, payload);

/**
 * Removes a registration that never went anywhere.
 *
 * The server decides eligibility under a row lock, so this can 422 (the member
 * is Active, Expired, or has paid before) or 409 (a support ticket is still
 * open) even when the roster row looked deletable a moment ago.
 */
export const deleteMember = (memberId) => api.delete(`/v1/members/${memberId}`);

/**
 * Mints a NEW referral link. The response carries the plain token exactly once —
 * only its sha256 hash is stored server-side — so the caller must show it
 * immediately and must not expect to re-read it later.
 *
 * Rotating also invalidates the previous link, which makes this the
 * revoke-and-replace path as well.
 */
export const rotateReferralToken = (memberId, payload = {}) =>
  api.post(`/v1/members/${memberId}/referral-token/rotate`, payload);

export const revokeReferralToken = (memberId) =>
  api.delete(`/v1/members/${memberId}/referral-token`);

/**
 * Issues or resets a member's portal login. The temporary password comes back
 * in this response and nowhere else — show it once, then it is gone.
 */
export const resetMemberPassword = (memberId) => api.post(`/v1/members/${memberId}/reset-password`, {}, {
  headers: { 'Idempotency-Key': (crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`) },
});

/**
 * Queues a roster CSV (Rule 66) and returns a history row to poll. The file is
 * built by a background job and handed back as a share URL when it is ready.
 */
export const createMemberExport = (filters = {}) => api.post('/v1/member-exports', filters, {
  headers: { 'Idempotency-Key': (crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`) },
});

export const getMemberExport = (exportId) => api.get(`/v1/member-exports/${exportId}`);
